import { AxiosInstance } from 'axios'

import { apiPaths } from '../apiPaths'
import { PBPListResponse, PBPResponse, PBPGetResponse } from '../types/pbpResponses.types'

import {
  GetProgramParams,
  GetCandidatesParams,
  GetCandidateParams,
  Program,
  CandidateNominationsResponse,
  CandidateResponse,
  ProgramsResponse,
  CandidatesResponse,
  CreateGrantCandidateCommentParams,
  GetCandidateCommentsParams,
  CandidateComment,
  GetCandidateCommentRepliesParams,
  ProgramStatesResponse,
  GetMyTasksParams,
  Task,
  GetCandidateFormParams,
  PostCandidateFormParams,
  CandidateFormSubmissionsResponse,
  TaskAssignee,
  UpdateTaskAssigneeParams,
  MarkTaskWontDoParams,
  CandidateForm,
  CandidateDocument,
  CandidateHistory,
  UploadCandidateDocumentsParams,
  UploadCandidateDocumentsResponse,
  GetCandidatesReportParams,
  GetProgramFormQuestionsResponse,
  GetCandidatesReportResponse,
  SubmitNominationParams
} from './grantsService.types'

type grantsServiceCapabilities = {
  percentClient: AxiosInstance
}

const { v1Grants } = apiPaths
const v1GrantsPrograms = `${v1Grants}/programs`

export const grantsService = ({ percentClient }: grantsServiceCapabilities) => ({
  getPrograms: () => percentClient.get<ProgramsResponse>(v1GrantsPrograms),
  getProgram: ({ programId }: GetProgramParams) =>
    percentClient.get<PBPResponse<Program>>(`${v1GrantsPrograms}/${programId}`),
  getProgramStates: ({ programId }: GetProgramParams) =>
    percentClient.get<ProgramStatesResponse>(`${v1GrantsPrograms}/${programId}/states`),
  getCandidates: (params: GetCandidatesParams) => {
    const { programId, ...queryParams } = params

    return percentClient.get<CandidatesResponse>(`${v1GrantsPrograms}/${programId}/candidates`, {
      params: queryParams
    })
  },
  getCandidate: ({ programId, candidateId }: GetCandidateParams) =>
    percentClient.get<PBPResponse<CandidateResponse>>(`${v1GrantsPrograms}/${programId}/candidates/${candidateId}`),
  getCandidateDocuments: ({ candidateId }: { candidateId: string }) =>
    percentClient.get<PBPResponse<CandidateDocument[]>>(`${v1Grants}/candidates/${candidateId}/documents`),
  uploadCandidateDocuments: ({ candidateId, formData, ...queryParams }: UploadCandidateDocumentsParams) => {
    return percentClient.post<UploadCandidateDocumentsResponse>(
      `${v1Grants}/candidates/${candidateId}/documents`,
      formData,
      {
        params: queryParams
      }
    )
  },
  getCandidateHistory: ({ programId, candidateId }: GetCandidateParams) =>
    percentClient.get<PBPResponse<CandidateHistory>>(
      `${v1GrantsPrograms}/${programId}/candidates/${candidateId}/history`
    ),
  getCandidateNominations: ({ programId, candidateId }: GetCandidateParams) =>
    percentClient.get<PBPResponse<CandidateNominationsResponse>>(
      `${v1GrantsPrograms}/${programId}/candidates/${candidateId}/nominations`
    ),
  getCandidateFormSubmissions: ({ programId, candidateId }: GetCandidateParams) =>
    percentClient.get<PBPResponse<CandidateFormSubmissionsResponse>>(
      `${v1GrantsPrograms}/${programId}/candidates/${candidateId}/form-submissions`
    ),
  getCandidateReviewForm: ({ programId, candidateId, formId }: GetCandidateFormParams) =>
    percentClient.get<PBPResponse<CandidateForm>>(
      `${v1GrantsPrograms}/${programId}/candidates/${candidateId}/forms/${formId}`
    ),
  postCandidateForm: ({ programId, candidateId, ...payload }: PostCandidateFormParams) =>
    percentClient.post(`${v1GrantsPrograms}/${programId}/candidates/${candidateId}/form-submissions`, payload),
  getCandidateComments: ({ programId, candidateId, ...params }: GetCandidateCommentsParams) =>
    percentClient.get<PBPListResponse<CandidateComment>>(
      `${v1GrantsPrograms}/${programId}/candidates/${candidateId}/comments`,
      { params: { ...params } }
    ),
  createCandidateComment: ({
    programId,
    candidateId,
    content,
    visibility = 'public',
    parentId
  }: CreateGrantCandidateCommentParams) =>
    percentClient.post(`${v1GrantsPrograms}/${programId}/candidates/${candidateId}/comments`, {
      content,
      visibility,
      parentId
    }),
  getCandidateCommentReplies: ({ programId, candidateId, commentId }: GetCandidateCommentRepliesParams) =>
    percentClient.get<PBPResponse<CandidateComment[]>>(
      `${v1GrantsPrograms}/${programId}/candidates/${candidateId}/comments/${commentId}/replies`
    ),
  getMyTasksV2: (params: GetMyTasksParams) =>
    percentClient.get<PBPListResponse<Task>>(`/v2/grants/my/tasks`, { params }),
  getTaskAssignees: () => percentClient.get<PBPGetResponse<TaskAssignee[]>>(`${apiPaths.v1Grants}/task-assignees`),
  updateTaskAssignee: (params: UpdateTaskAssigneeParams) =>
    percentClient.put(`${apiPaths.v1Grants}/task-assignees`, params),
  markTaskWontDo: ({ programId, candidateId, taskId }: MarkTaskWontDoParams) =>
    percentClient.patch(`${v1GrantsPrograms}/${programId}/candidates/${candidateId}/tasks/${taskId}/wont-do`),
  getAllFormQuestions: ({ programId }: GetProgramParams) =>
    percentClient.get<PBPResponse<GetProgramFormQuestionsResponse>>(`${v1GrantsPrograms}/${programId}/form-questions`),
  getReport: ({ programId, questionIds, ...params }: GetCandidatesReportParams) =>
    percentClient.post<PBPResponse<GetCandidatesReportResponse>>(
      `${v1GrantsPrograms}/${programId}/candidates/report`,
      { questionIds },
      { params }
    ),
  submitNomination: ({ programId, ...payload }: SubmitNominationParams) =>
    percentClient.post(`${v1GrantsPrograms}/${programId}/nominations`, payload)
})
